import React, { useState, useEffect } from "react";
import {
  Container, Typography, Button, Box, IconButton, useMediaQuery, Switch,
  Tooltip, Avatar, Chip
} from '@mui/material';
import moment from 'moment';
import { useTheme } from '@mui/material/styles';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import AssessmentIcon from '@mui/icons-material/Assessment';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import DataTable from '../../components/dataTable';
import AddUserDialog from './AddUserDialog';
import DeleteUserDialog from './DeleteUserDialog';
import ResetPasswordDialog from './ResetPasswordDialog';
import UserSummaryDialog from './UserSummaryDialog';
import { addUser, getAllUsers, deleteUser, updateUser, adminResetPassword } from '../../api/userAPI';
import useSnackbar from '../../hooks/useSnackbar';

const Users = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();
  const { user } = useSelector((state) => state.tokens);
  const { showSnackbar, SnackbarComponent } = useSnackbar();

  const [summaryDialogOpen, setSummaryDialogOpen] = useState(false);
  const [selectedSummaryUser, setSelectedSummaryUser] = useState(null);

  const [userAdded, setUserAdded] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogDelOpen, setDialogDelOpen] = useState(false);
  const [resetDialogOpen, setResetDialogOpen] = useState(false);
  const [selectedResetUser, setSelectedResetUser] = useState(null);
  const [delUserId, setDelUserId] = useState('');
  const [allUsers, setAllUsers] = useState([]);
  const initialFormState = {
    fullName: '',
    email: '',
    password: '',
    confirmPassword: '', 
    permission: '',
    isOfficeEmp: false,
    isTeamLeader: false,
    teamLeader: '',
    department: '',
    dob: '',
    officeJoiningDate: '',
    bloodGroup: '',
  };

  const [userFormData, setUserFormData] = useState(initialFormState);

  const loggedInUserIsAdmin = user?.permission === "Admin" || user?.permission === "Admin-a" || user?.permission === "HR Management";

  useEffect(() => {
    getAllUsers()
      .then((res) => setAllUsers(res.data))
      .catch((err) => console.error(err));
  }, [userAdded]);

  const handleAddUserClick = () => {
    setUserFormData(initialFormState);
    setDialogOpen(true);
  };

  const handleEditUserClick = (selectedRow) => {
    setUserFormData({
      ...selectedRow,
      dob: selectedRow.dob ? moment(selectedRow.dob).format('YYYY-MM-DD') : (selectedRow.dateOfBirth ? moment(selectedRow.dateOfBirth).format('YYYY-MM-DD') : ''),
      officeJoiningDate: selectedRow.officeJoiningDate ? moment(selectedRow.officeJoiningDate).format('YYYY-MM-DD') : '',
      bloodGroup: selectedRow.bloodGroup || '',
      teamLeader: selectedRow.teamLeader?._id || selectedRow.teamLeader?.id || selectedRow.teamLeader || '',
    });
    setDialogOpen(true);
  };

  const handleDialogClose = () => setDialogOpen(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setUserFormData({ ...userFormData, [name]: value });
  };

  const handleAddUserSubmit = (e) => {
    e.preventDefault();
    const action = userFormData.id ? updateUser(userFormData, userFormData.id) : addUser(userFormData);

    action
      .then(() => {
        setUserFormData(initialFormState);
        setDialogOpen(false);
        setUserAdded(prev => !prev);
      })
      .catch(() => {
        setUserFormData(initialFormState);
        setDialogOpen(false);
        setUserAdded(false);
      });
  };

  const handleDeleteUser = (userId) => {
    setDelUserId(userId);
    setDialogDelOpen(true);
  };

  const handleDelClose = () => {
    setDelUserId('');
    setDialogDelOpen(false);
  };

  const handleDelUserSubmit = () => {
    deleteUser(delUserId)
      .then(() => {
        getAllUsers().then((res) => setAllUsers(res.data));
        handleDelClose();
      })
      .catch(() => handleDelClose());
  };

  const handleToggleActive = (userRow) => {
    const updatedUser = { ...userRow, isActive: !userRow.isActive };
    updateUser(updatedUser, userRow.id)
      .then(() => {
        getAllUsers().then((res) => setAllUsers(res.data));
        showSnackbar(`User status updated to ${updatedUser.isActive ? 'Active' : 'Inactive'}`, "success");
      })
      .catch(() => {
        showSnackbar("Failed to update user status", "error");
      });
  };

  const handleToggleOfficeEmp = (userRow) => {
    const updatedUser = { ...userRow, isOfficeEmp: !userRow.isOfficeEmp };
    updateUser(updatedUser, userRow.id)
      .then(() => {
        getAllUsers().then((res) => setAllUsers(res.data));
        showSnackbar(`User updated as ${updatedUser.isOfficeEmp ? 'Office Employee' : 'Not Office Employee'}`, "success");
      })
      .catch(() => {
        showSnackbar("Failed to update user status", "error");
      });
  };

  const handleToggleIsLeader = (userRow) => {
    const currentVal = Boolean(userRow.isTeamLeader || userRow.is_leader || userRow.isLeader);
    const updatedStatus = !currentVal;
    const updatedUser = { 
      ...userRow, 
      isTeamLeader: updatedStatus,
      is_leader: updatedStatus,
      isLeader: updatedStatus
    };
    updateUser(updatedUser, userRow.id)
      .then(() => {
        getAllUsers().then((res) => setAllUsers(res.data));
        showSnackbar(`User updated as ${updatedStatus ? 'Team Leader' : 'Not Team Leader'}`, "success");
      })
      .catch(() => {
        showSnackbar("Failed to update user status", "error");
      });
  };

  const handleResetPasswordClick = (selectedRow) => {
    setSelectedResetUser(selectedRow);
    setResetDialogOpen(true);
  };

  const handleResetPasswordSubmit = (userId, newPassword) => {
    return adminResetPassword(userId, newPassword)
      .then((res) => {
        showSnackbar(res.data?.message || "Password updated successfully.", "success");
        setResetDialogOpen(false);
      })
      .catch((err) => {
        showSnackbar(err.response?.data?.message || "Failed to reset password", "error");
        throw err;
      });
  };

  const handleViewSummary = (userRow) => {
    setSelectedSummaryUser(userRow);
    setSummaryDialogOpen(true);
  };

  const columns = [
    {
      field: 'fullName',
      headerName: 'Name',
      width: 200,
      flex: 1.2,
      renderCell: (params) => (
        <Tooltip title="Click to view full summary (Attendance, Leaves, Breaks)">
          <Box
            onClick={(e) => {
              e.stopPropagation();
              handleViewSummary(params.row);
            }}
            sx={{
              cursor: 'pointer',
              color: '#1976d2',
              fontWeight: 700,
              display: 'flex',
              alignItems: 'center',
              gap: 1.2,
              '&:hover': {
                textDecoration: 'underline',
                color: '#1565c0'
              }
            }}
          >
            <Avatar
              sx={{
                width: 32,
                height: 32,
                fontSize: '0.85rem',
                bgcolor: '#e3f2fd',
                color: '#1976d2',
                fontWeight: 'bold',
                border: '1px solid #90caf9'
              }}
            >
              {params.row.fullName ? params.row.fullName.charAt(0).toUpperCase() : 'U'}
            </Avatar>
            <span>{params.row.fullName}</span>
          </Box>
        </Tooltip>
      )
    },
    { field: 'email', headerName: 'Email', width: 180, flex: 1 },
    { field: 'permission', headerName: 'Permission', width: 140, flex: 1 },
    { field: 'department', headerName: 'Department', width: 120, flex: 1 },
    {
      field: 'bloodGroup',
      headerName: 'Blood Group',
      width: 110,
      renderCell: (params) => (
        params.row.bloodGroup ? (
          <Chip 
            label={params.row.bloodGroup} 
            size="small" 
            sx={{ 
              fontWeight: 700,
              bgcolor: '#ffebee', 
              color: '#c62828',
              border: '1px solid #ffcdd2'
            }} 
          />
        ) : '-'
      ),
    },
    {
      field: 'dob',
      headerName: 'DOB',
      width: 120,
      renderCell: (params) => (
        params.row.dob || params.row.dateOfBirth ? moment(params.row.dob || params.row.dateOfBirth).format('DD MMM YYYY') : '-'
      ),
    },
    {
      field: 'officeJoiningDate',
      headerName: 'Joining Date',
      width: 120,
      renderCell: (params) => (
        params.row.officeJoiningDate ? moment(params.row.officeJoiningDate).format('DD MMM YYYY') : '-'
      ),
    },
    {
      field: 'teamLeader',
      headerName: 'Team Leader',
      width: 140,
      renderCell: (params) => params.row.teamLeader ? params.row.teamLeader.fullName : 'None',
      flex: 1
    },
    {
      field: 'isTeamLeader',
      headerName: 'Is Leader',
      width: 100,
      renderCell: (params) => (
        <Switch
          checked={Boolean(params.row.isTeamLeader || params.row.is_leader || params.row.isLeader)}
          onChange={() => handleToggleIsLeader(params.row)}
          color="primary"
          disabled={!loggedInUserIsAdmin || params.row.id === user?._id}
        />
      ),
    },
    {
      field: 'isOfficeEmp',
      headerName: 'Office Emp',
      width: 100,
      renderCell: (params) => (
        <Switch
          checked={params.row.isOfficeEmp || false}
          onChange={() => handleToggleOfficeEmp(params.row)}
          color="primary"
          disabled={!loggedInUserIsAdmin || params.row.id === user?._id}
        />
      ),
    },
    {
      field: 'isActive',
      headerName: 'Active',
      width: 100,
      renderCell: (params) => (
        <Switch
          checked={params.row.isActive !== false}
          onChange={() => handleToggleActive(params.row)}
          color="primary"
          disabled={!loggedInUserIsAdmin || params.row.id === user?._id}
        />
      ),
    },
    {
      field: 'action',
      headerName: 'Actions',
      width: 200,
      renderCell: (params) => (
        <Box display="flex" gap={0.5}>
          <Tooltip title="View Summary (Attendance, Leaves, Breaks)">
            <IconButton color="info" onClick={() => handleViewSummary(params.row)}>
              <AssessmentIcon />
            </IconButton>
          </Tooltip>
          <IconButton color="primary" onClick={() => handleEditUserClick(params.row)}>
            <EditIcon />
          </IconButton>
          {loggedInUserIsAdmin && (
            <IconButton color="secondary" onClick={() => handleResetPasswordClick(params.row)}>
              <VpnKeyIcon />
            </IconButton>
          )}
          <IconButton color="error" onClick={() => handleDeleteUser(params.row.id)}>
            <DeleteIcon />
          </IconButton>
        </Box>
      ),
    }
  ];

  return (
    <Container maxWidth="xl" sx={{ pt: 3, pb: 6 }}>
      <Box
        display="flex"
        justifyContent="space-between"
        flexDirection={isMobile ? 'column' : 'row'}
        alignItems={isMobile ? 'stretch' : 'center'}
        gap={2}
        mb={4}
        sx={{
          backgroundColor: 'white',
          p: 3,
          borderRadius: 3,
          boxShadow: '0 4px 20px rgba(0,0,0,0.03)',
        }}
      >
        <Typography 
          variant={isMobile ? "h5" : "h4"} 
          fontWeight="800" 
          sx={{
            background: 'linear-gradient(45deg, #1976d2 30%, #42a5f5 90%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            letterSpacing: '-0.5px'
          }}
        >
          Manage Users
        </Typography>
        <Button
          variant="contained"
          onClick={handleAddUserClick}
          sx={{ 
            width: isMobile ? '100%' : 'auto',
            background: 'linear-gradient(45deg, #1976d2 30%, #42a5f5 90%)',
            boxShadow: '0 3px 10px rgba(25, 118, 210, 0.3)',
            borderRadius: 2,
            px: 4,
            py: 1,
            textTransform: 'none',
            fontWeight: 'bold',
            fontSize: '1rem',
            transition: 'all 0.3s',
            '&:hover': {
              transform: 'translateY(-2px)',
              boxShadow: '0 6px 15px rgba(25, 118, 210, 0.4)',
            }
          }}
        >
          + Add New User
        </Button>
      </Box>

      <DataTable
        rows={allUsers}
        columns={columns}
        onRowClick={(params) => handleViewSummary(params.row)}
      />

      <UserSummaryDialog
        open={summaryDialogOpen}
        user={selectedSummaryUser}
        allUsers={allUsers}
        onClose={() => setSummaryDialogOpen(false)}
      />

      <AddUserDialog
        open={dialogOpen}
        userFormData={userFormData}
        allUsers={allUsers}
        handleAddUserSubmit={handleAddUserSubmit}
        handleChange={handleChange}
        handleClose={handleDialogClose}
      />

      <DeleteUserDialog
        open={dialogDelOpen}
        handleDelUserSubmit={handleDelUserSubmit}
        handleDelClose={handleDelClose}
      />

      <ResetPasswordDialog
        open={resetDialogOpen}
        user={selectedResetUser}
        handleClose={() => setResetDialogOpen(false)}
        handleResetSubmit={handleResetPasswordSubmit}
      />

      <SnackbarComponent />
    </Container>
  );
};

export default Users;
